/**
 * Single source of truth for preview_eval's evaluation budget and the timeout
 * guidance the model sees. Shared by the client handler (ui/previewEvalClient)
 * and the worker tool (tools/preview-eval) so the number and the message can't
 * drift across the surfaces that state them (the client timeout error, the
 * worker's timed-out render + fallback, the tool description, the systemPrompt).
 *
 * Pure constants — NO imports — so both the browser and the worker bundles can
 * import it freely.
 *
 * NOTE on cancellation: this ceiling is a PARENT-SIDE reply deadline, not a kill
 * — the evaluated snippet keeps running in the sandboxed preview after the timer
 * fires (features/files/api/preview-snapshot.ts has no AbortController). The
 * message therefore steers to a SERVER-SIDE operation, which the app-scripts
 * skill teaches idempotently (INSERT OR REPLACE / UNIQUE), so a redo after a
 * partially-completed loop-write does not double-count. True cancellation of an
 * in-flight eval is a separate platform change, tracked outside this module.
 */

/** Wall-clock budget the parent waits for a preview_eval reply. */
export const EVAL_TIMEOUT_MS = 15_000

/** The same budget in whole seconds, for the model-facing copy. */
export const EVAL_TIMEOUT_SECONDS = EVAL_TIMEOUT_MS / 1000

/**
 * The actionable, app-agnostic timeout message: states the budget, names the
 * wrong shape ("don't loop"), points at the GENERAL SDK surfaces (a batch
 * `papan.db.run`, or a `papan.scripts.run` script), and surfaces the hidden
 * `app-scripts` skill by name. Carries NO one-app plumbing (`_scripts`/ingest/
 * trigger) on purpose — those are one app's layout, not the platform's.
 */
export const PREVIEW_EVAL_TIMEOUT_MESSAGE =
  `preview_eval timed out — it evaluates for up to ${EVAL_TIMEOUT_SECONDS}s and is for quick inspection, ` +
  "not bulk or long-running work. Don't loop operations here; do the work as a single " +
  'server-side operation (one batch papan.db.run, or a papan.scripts.run script) — ' +
  'see the `app-scripts` skill for the pattern that fits this app.'
